import { h as __h } from "preact";
import { Component, h } from "preact";
import { enforcedPoliciesResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, query, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
/** @import { orgResponse } from "~/backend/validate.js"; */

import Data from "~/component/Data.js";
import Section from "~/component/Section.js";
import Select from "~/component/Select.js";
import Switch from "~/component/Switch.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./EnforcedPolicies.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./EnforcedPolicies.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 */
export default class EnforcedPolicies extends Component {
  policies = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/enforced-policies`]
  }), {
    schema: response(enforcedPoliciesResponse)
  });
  render() {
    const policies = this.policies.data?.data;
    return __h(Section, {
      title: "Enforced Policies",
      description: "These policies are enforced on all computers in this organization.",
      icon: "shield-lock"
    }, h(Data, {
      loading: this.policies.loading && !policies,
      error: this.policies.error,
      children: policies && __h("div", {
        "class": css.policies
      }, __h(ClipboardPolicySelect, {
        orgId: this.props.org.id,
        policies: policies
      }), __h(PrivacyModeToggle, {
        orgId: this.props.org.id,
        policies: policies
      }), __h(UsbPassthroughSwitch, {
        orgId: this.props.org.id,
        policies: policies
      }), __h(MaxBitrateSelect, {
        orgId: this.props.org.id,
        policies: policies
      }))
    }));
  }
}

/**
 * @extends {Component<ClipboardPolicySelectProps>}
 *
 * @typedef {object} ClipboardPolicySelectProps
 * @property {string} orgId
 * @property {enforcedPoliciesResponse} policies
 */
class ClipboardPolicySelect extends Component {
  #update = new PromiseSignal(async (/** @type {string} */clipboardPolicy) => {
    const {
      orgId
    } = this.props;
    try {
      await authedApiRequest("PATCH", `/v2/orgs/${orgId}/enforced-policies`, {
        json: {
          clipboard_policy: clipboardPolicy
        }
      });
      Query.invalidate([`/v2/orgs/${orgId}/enforced-policies`]);
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });
  render() {
    return __h("div", {
      "class": css.policyRow
    }, __h("div", {
      "class": css.policyInfo
    }, __h("label", {
      "class": css.policyLabel,
      "for": "clipboard_policy"
    }, "Clipboard"), __h("span", {
      "class": css.policyDescription
    }, "Restrict copying and pasting between client and host computers. Supported on Phaze 0.8.1 and later.")), __h(Select, {
      id: "clipboard_policy",
      value: this.props.policies.clipboard_policy,
      disabled: this.#update.loading,
      onChange: (/** @type {Event} */e) => this.#update.invoke(/** @type {HTMLSelectElement} */e.currentTarget.value)
    }, __h("option", {
      value: "allowed"
    }, "Allow copy and paste"), __h("option", {
      value: "blocked"
    }, "Block copy and paste"), __h("option", {
      value: "host_copy_blocked"
    }, "Block copying from host to client")));
  }
}

/**
 * @extends {Component<PrivacyModeToggleProps>}
 *
 * @typedef {object} PrivacyModeToggleProps
 * @property {string} orgId
 * @property {enforcedPoliciesResponse} policies
 */
class PrivacyModeToggle extends Component {
  #toggle = new PromiseSignal(async () => {
    const {
      orgId,
      policies
    } = this.props;
    try {
      await authedApiRequest("PATCH", `/v2/orgs/${orgId}/enforced-policies`, {
        json: {
          is_privacy_mode_enforced: !policies.is_privacy_mode_enforced
        }
      });
      Query.invalidate([`/v2/orgs/${orgId}/enforced-policies`]);
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });
  render() {
    return __h("div", {
      "class": css.policyRow
    }, __h("div", {
      "class": css.policyInfo
    }, __h("span", {
      "class": css.policyLabel
    }, "Privacy Mode"), __h("span", {
      "class": css.policyDescription
    }, "Blank the host computer's displays, mute its audio, and lock it when the client disconnects. Supported on Phaze 0.8.2 and later.")), __h(Switch, {
      checked: this.props.policies.is_privacy_mode_enforced,
      loading: this.#toggle.loading,
      "aria-label": "Privacy Mode",
      onChange: this.#toggle.invoke
    }));
  }
}

/**
 * @extends {Component<UsbPassthroughSwitchProps>}
 *
 * @typedef {object} UsbPassthroughSwitchProps
 * @property {string} orgId
 * @property {enforcedPoliciesResponse} policies
 */
class UsbPassthroughSwitch extends Component {
  #update = new PromiseSignal(async (/** @type {boolean} */isBlocked) => {
    const {
      orgId
    } = this.props;
    try {
      await authedApiRequest("PATCH", `/v2/orgs/${orgId}/enforced-policies`, {
        json: {
          is_usb_passthrough_blocked: isBlocked
        }
      });
      Query.invalidate([`/v2/orgs/${orgId}/enforced-policies`]);
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });
  render() {
    return __h("div", {
      "class": css.policyRow
    }, __h("div", {
      "class": css.policyInfo
    }, __h("label", {
      "class": css.policyLabel,
      "for": "usb_passthrough_blocked"
    }, "Block USB passthrough"), __h("span", {
      "class": css.policyDescription
    }, "Stop USB devices connected to clients from being passed through to hosts. Supported on Phaze 0.9.0 and later.")), __h(Switch, {
      id: "usb_passthrough_blocked",
      checked: this.props.policies.is_usb_passthrough_blocked,
      loading: this.#update.loading,
      onChange: (/** @type {Event} */e) => this.#update.invoke(/** @type {HTMLInputElement} */e.currentTarget.checked)
    }));
  }
}
const BITRATE_PRESETS_MBPS = [5, 10, 20, 30, 40, 50];

/**
 * @extends {Component<MaxBitrateSelectProps>}
 *
 * @typedef {object} MaxBitrateSelectProps
 * @property {string} orgId
 * @property {enforcedPoliciesResponse} policies
 */
class MaxBitrateSelect extends Component {
  #update = new PromiseSignal(async (/** @type {number} */maxBitrateMbps) => {
    const {
      orgId
    } = this.props;
    try {
      await authedApiRequest("PATCH", `/v2/orgs/${orgId}/enforced-policies`, {
        json: {
          max_bitrate_mbps: maxBitrateMbps
        }
      });
      Query.invalidate([`/v2/orgs/${orgId}/enforced-policies`]);
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  });
  render() {
    return __h("div", {
      "class": css.policyRow
    }, __h("div", {
      "class": css.policyInfo
    }, __h("label", {
      "class": css.policyLabel,
      "for": "max_bitrate"
    }, "Maximum bit rate"), __h("span", {
      "class": css.policyDescription
    }, "Limit the bit rate of the stream from the host computer to the client. Supported on Phaze 0.9.1 and later.")), __h(Select, {
      id: "max_bitrate",
      value: this.props.policies.max_bitrate_mbps,
      disabled: this.#update.loading,
      onChange: (/** @type {Event} */e) => this.#update.invoke(Number(/** @type {HTMLSelectElement} */e.currentTarget.value))
    }, __h("option", {
      value: "0"
    }, "No limit"), BITRATE_PRESETS_MBPS.map(mbps => __h("option", {
      value: mbps
    }, mbps, " Mbps"))));
  }
}